import { useState, useEffect, useCallback } from "react";
import ConversationProjetV2 from "../components/ConversationProjetV2";
import { getConversationsListe } from "../services/api";

// ===================================================================
// The Crowd v2 — Conversations
//
// Adaptation v2 de pages/Conversations.js : memes appels API, meme tri
// (plus recent d'abord). Disposition plein ecran : la liste a gauche,
// le fil selectionne a droite, sur toute la hauteur disponible.
//
// Non-lus : le badge d'une conversation s'efface des que son fil est
// ouvert et marque lu (signal onNonLusChange du fil). Apres un envoi, la
// liste est rechargee sans indicateur de chargement pour mettre a jour
// l'apercu et l'ordre.
//
// Ecran etroit : un seul panneau a la fois — la liste, puis le fil avec
// un bouton retour (bascule geree en CSS, cf. v2-conversations.css).
// ===================================================================

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function trierParRecence(data) {
  return [...data].sort((a, b) => {
    const da = a.dernier_message?.created_at || "";
    const db = b.dernier_message?.created_at || "";
    return db.localeCompare(da);
  });
}

export default function ConversationsV2({ collaborateur }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [selected, setSelected] = useState(null); // { projet_id, projet_nom }

  const collabId = collaborateur?.id;

  // silencieux = rechargement en arriere-plan (apres un envoi), sans
  // remplacer la liste par l'indicateur de chargement
  const charger = useCallback(async (silencieux = false) => {
    if (!collabId) return;
    if (!silencieux) setLoading(true);
    setErreur(null);
    try {
      const data = await getConversationsListe(collabId);
      setConversations(trierParRecence(data));
    } catch (e) {
      setErreur(e.message || "Impossible de charger les conversations.");
    } finally {
      if (!silencieux) setLoading(false);
    }
  }, [collabId]);

  useEffect(() => { charger(); }, [charger]);

  const marquerLue = (projetId) => {
    setConversations((prev) =>
      prev.map((c) => (c.projet_id === projetId ? { ...c, non_lus: 0 } : c))
    );
  };

  const fermer = () => {
    setSelected(null);
    charger(true);
  };

  return (
    <div className={`v2-page v2-page-module v2-page-conversations${selected ? " v2-conv-avec-fil" : ""}`}>
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Conversations</h1>
        <p className="v2-sous-titre">Échanges sur les projets auxquels vous participez.</p>
      </header>

      <div className="v2-conv-colonnes">
        {/* ── Liste ── */}
        <section className="v2-panneau v2-conv-liste" aria-label="Liste des conversations">
          {loading && <div className="v2-chargement">Chargement…</div>}
          {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}

          {!loading && !erreur && conversations.length === 0 && (
            <p className="v2-vide">Aucune conversation pour l'instant.</p>
          )}

          {!loading && conversations.length > 0 && (
            <ul className="v2-conv-items">
              {conversations.map((c) => {
                const actif = selected?.projet_id === c.projet_id;
                return (
                  <li key={c.projet_id}>
                    <button
                      type="button"
                      className={`v2-conv-item${actif ? " actif" : ""}`}
                      aria-current={actif ? "true" : undefined}
                      onClick={() => setSelected({ projet_id: c.projet_id, projet_nom: c.projet_nom })}
                    >
                      <span className="v2-conv-item-entete">
                        <span className="v2-conv-item-projet">{c.projet_nom}</span>
                        {c.non_lus > 0 && (
                          <span className="v2-conv-non-lus" aria-label={`${c.non_lus} non lu${c.non_lus > 1 ? "s" : ""}`}>
                            {c.non_lus}
                          </span>
                        )}
                      </span>
                      {c.dernier_message ? (
                        <span className="v2-conv-item-apercu">
                          <strong>{c.dernier_message.auteur_nom} : </strong>
                          {c.dernier_message.contenu}
                        </span>
                      ) : (
                        <span className="v2-conv-item-apercu v2-conv-item-apercu-vide">Aucun message</span>
                      )}
                      {c.dernier_message && (
                        <span className="v2-conv-item-date">{formatDate(c.dernier_message.created_at)}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ── Fil ── */}
        <section className="v2-panneau v2-conv-fil" aria-label="Fil de discussion">
          {selected && collabId ? (
            <>
              <div className="v2-conv-fil-entete">
                <button type="button" className="v2-btn v2-btn-secondaire v2-conv-retour" onClick={fermer}>
                  ← Conversations
                </button>
                <h2 className="v2-conv-fil-titre">{selected.projet_nom}</h2>
              </div>
              <ConversationProjetV2
                key={selected.projet_id}
                projetId={selected.projet_id}
                collaborateurId={collabId}
                onNonLusChange={() => marquerLue(selected.projet_id)}
                onMessageEnvoye={() => charger(true)}
              />
            </>
          ) : (
            <div className="v2-conv-fil-vide">
              <p className="v2-vide">Sélectionnez une conversation dans la liste.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
